import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../../../utils";
import { GetAllGGNotificationLotDTO, GetAllGGNotificationLotFiltersDTO, GetAllGGNotificationTabDTO, GetAllGGNotificationTabFiltersDTO, GGNotificationLotDTO, GGNotificationTabDTO } from "./dataservice/dto";
import { NewGGNotificationCoCService } from "./dataservice/service";

interface GGNotificationCoCState {
    // REQUEST [LOT]
    lotCreationRequest: GGNotificationLotDTO

    // STATUS
    lotCreationStatus: PromiseStatuses
    lotDeleteStatus: PromiseStatuses
    getAllLotStatus: PromiseStatuses
    getLotByIdStatus: PromiseStatuses

    // RESPONSE
    getAllLotResponse?: GetAllGGNotificationLotDTO
    getLotByIdResponse?: GGNotificationLotDTO

    ////////////////////////////////////////////

    // REQUEST [TAB]
    tabUpdateRequest: GGNotificationTabDTO
    tabIdToUpdate: string

    // STATUS
    tabUpdateStatus: PromiseStatuses
    getAllTabStatus: PromiseStatuses
    getTabByIdStatus: PromiseStatuses

    // RESPONSE
    getAllTabResponse?: GetAllGGNotificationTabDTO
    getTabByIdResponse?: GGNotificationTabDTO
}

const initialState: GGNotificationCoCState = {
    // LOT
    lotCreationRequest: {},

    lotCreationStatus: 'idle',
    lotDeleteStatus: 'idle',
    getAllLotStatus: 'idle',
    getLotByIdStatus: 'idle',

    // TAB
    tabIdToUpdate: '',
    tabUpdateRequest: {
        cocTypes: [],
        partialTransformation: false,
        cocPartialTransformations: [],
        ggnLabelLogoLicense: false,
        subcontractedActivity: ''
    },

    tabUpdateStatus: 'idle',
    getAllTabStatus: 'idle',
    getTabByIdStatus: 'idle'
}

export const GetAllGGNotificationLot = createAsyncThunk(
    'GGNotificationLot/GetAll',
    async (filters: GetAllGGNotificationLotFiltersDTO, thunkApi): Promise<GetAllGGNotificationLotDTO> => {
        const GGNotificationCoCService = NewGGNotificationCoCService()
        return GGNotificationCoCService.GetAllGGNotificationLot(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetGGNotificationLotById = createAsyncThunk(
    'GGNotificationLot/GetById',
    async (id: string, thunkApi): Promise<GGNotificationLotDTO> => {
        const GGNotificationCoCService = NewGGNotificationCoCService()
        return GGNotificationCoCService.GetGGNotificationLotById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GGNotificationLotDelete = createAsyncThunk(
    'GGNotificationLot/Delete',
    async (id: string, thunkApi): Promise<void> => {
        const GGNotificationCoCService = NewGGNotificationCoCService()
        return GGNotificationCoCService.DeleteGGNotificationLot(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GGNotificationLotCreation = createAsyncThunk(
    'GGNotificationLot/Creation',
    async (data: GGNotificationLotDTO, thunkApi): Promise<void> => {
        const GGNotificationCoCService = NewGGNotificationCoCService()
        return GGNotificationCoCService.CreateGGNotificationLot(data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GGNotificationTabUpdate = createAsyncThunk(
    'GGNotificationTab/Update',
    async (request: {body: GGNotificationTabDTO, id: string}, thunkApi): Promise<void> => {
        const GGNotificationCoCService = NewGGNotificationCoCService()
        return GGNotificationCoCService.UpdateGGNotificationTab(request.body, request.id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetAllGGNotificationTab = createAsyncThunk(
    'GGNotificationTab/GetAll',
    async (filters: GetAllGGNotificationTabFiltersDTO, thunkApi): Promise<GetAllGGNotificationTabDTO> => {
        const GGNotificationCoCService = NewGGNotificationCoCService()
        return GGNotificationCoCService.GetAllGGNotificationTab(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetGGNotificationTabById = createAsyncThunk(
    'GGNotificationTab/GetById',
    async (id: string, thunkApi): Promise<GGNotificationTabDTO> => {
        const GGNotificationCoCService = NewGGNotificationCoCService()
        return GGNotificationCoCService.GetGGNotificationTabById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

const GGNotificationCoCSlice = createSlice({
    name: 'ggNotificationCoc/slice',
    initialState,
    reducers: {
        // LOT
        setLotCreationGGnotificationCOCTabId: (state, action) => {
            state.lotCreationRequest.ggNotificationCOCTabId = action.payload
        },
        setLotCreationAddress: (state, action) => {
            state.lotCreationRequest.address = action.payload
        },
        setLotCreationProducts: (state, action) => {
            state.lotCreationRequest.products = action.payload
        },
        setLotCreationTotalQuantity: (state, action) => {
            state.lotCreationRequest.totalQuantity = action.payload
        },
        setLotCreationManipulationPeriod: (state, action) => {
            state.lotCreationRequest.manipulationPeriod = action.payload
        },
        setLotCreationLabelling: (state, action) => {
            state.lotCreationRequest.labelling = action.payload
        },
        setLotCreationDestinationCountry: (state, action) => {
            state.lotCreationRequest.destinationCountry = action.payload
        },
        resetLotCreationRequest: (state) => {
            state.lotCreationRequest = {}
        },

        resetLotCreationStatus: (state) => {
            state.lotCreationStatus = 'idle'
        },
        resetLotDeleteStatus: (state) => {
            state.lotDeleteStatus = 'idle'
        },
        resetGetAllLotStatus: (state) => {
            state.getAllLotStatus = 'idle'
        },
        resetGetLotByIdStatus: (state) => {
            state.getLotByIdStatus = 'idle'
        },

        // TAB
        setTabUpdateRequest: (state, action) => {
            state.tabUpdateRequest = action.payload
        },
        setTabIdToUpdate: (state, action) => {
            state.tabIdToUpdate = action.payload
        },
        setTabUpdateGGNotificationId: (state, action) => {
            state.tabUpdateRequest.ggNotificationId = action.payload
        },
        setTabUpdateCocTypes: (state, action) => {
            state.tabUpdateRequest.cocTypes = action.payload
        },
        setTabUpdatePartialTransformation: (state, action) => {
            state.tabUpdateRequest.partialTransformation = action.payload
        },
        setTabUpdateCocPartialTransformations: (state, action) => {
            state.tabUpdateRequest.cocPartialTransformations = action.payload
        },
        setTabUpdateGGNLabelLogoLicense: (state, action) => {
            state.tabUpdateRequest.ggnLabelLogoLicense = action.payload
        },
        setTabUpdateSubcontractedActivity: (state, action) => {
            state.tabUpdateRequest.subcontractedActivity = action.payload
        },
        resetTabUpdateRequest: (state) => {
            state.tabUpdateRequest = {
                cocTypes: [],
                partialTransformation: false,
                cocPartialTransformations: [],
                ggnLabelLogoLicense: false,
                subcontractedActivity: ''
            }
        },

        resetTabUpdateStatus: (state) => {
            state.tabUpdateStatus = 'idle'
        },
        resetGetAllTabStatus: (state) => {
            state.getAllTabStatus = 'idle'
        },
        resetGetTabByIdStatus: (state) => {
            state.getTabByIdStatus = 'idle'
        }
    },
    extraReducers(builder) {
        builder

            // GET ALL
            .addCase(GetAllGGNotificationLot.pending, (state) => {
                state.getAllLotStatus = 'loading'
            })
            .addCase(GetAllGGNotificationLot.fulfilled, (state, action) => {
                state.getAllLotStatus = 'successfully'
                state.getAllLotResponse = action.payload
            })
            .addCase(GetAllGGNotificationLot.rejected, (state, action) => {
                state.getAllLotStatus = 'failed'
            })

            // GET BY ID
            .addCase(GetGGNotificationLotById.pending, (state) => {
                state.getLotByIdStatus = 'loading'
            })
            .addCase(GetGGNotificationLotById.fulfilled, (state, action) => {
                state.getLotByIdStatus = 'successfully'
                state.getLotByIdResponse = action.payload
            })
            .addCase(GetGGNotificationLotById.rejected, (state, action) => {
                state.getLotByIdStatus = 'failed'
            })

            // CREATION
            .addCase(GGNotificationLotCreation.pending, (state) => {
                state.lotCreationStatus = 'loading'
            })
            .addCase(GGNotificationLotCreation.fulfilled, (state) => {
                state.lotCreationStatus = 'successfully'
            })
            .addCase(GGNotificationLotCreation.rejected, (state, action) => {
                state.lotCreationStatus = 'failed'
            })

            // DELETE
            .addCase(GGNotificationLotDelete.pending, (state) => {
                state.lotDeleteStatus = 'loading'
            })
            .addCase(GGNotificationLotDelete.fulfilled, (state) => {
                state.lotDeleteStatus = 'successfully'
            })
            .addCase(GGNotificationLotDelete.rejected, (state, action) => {
                state.lotDeleteStatus = 'failed'
            })

            // GET ALL
            .addCase(GetAllGGNotificationTab.pending, (state) => {
                state.getAllTabStatus = 'loading'
            })
            .addCase(GetAllGGNotificationTab.fulfilled, (state, action) => {
                state.getAllTabStatus = 'successfully'
                state.getAllTabResponse = action.payload
            })
            .addCase(GetAllGGNotificationTab.rejected, (state, action) => {
                state.getAllTabStatus = 'failed'
            })

            // GET BY ID
            .addCase(GetGGNotificationTabById.pending, (state) => {
                state.getTabByIdStatus = 'loading'
            })
            .addCase(GetGGNotificationTabById.fulfilled, (state, action) => {
                state.getTabByIdStatus = 'successfully'
                state.getTabByIdResponse = action.payload
            })
            .addCase(GetGGNotificationTabById.rejected, (state, action) => {
                state.getTabByIdStatus = 'failed'
            })

            // CREATION
            .addCase(GGNotificationTabUpdate.pending, (state) => {
                state.tabUpdateStatus = 'loading'
            })
            .addCase(GGNotificationTabUpdate.fulfilled, (state) => {
                state.tabUpdateStatus = 'successfully'
            })
            .addCase(GGNotificationTabUpdate.rejected, (state, action) => {
                state.tabUpdateStatus = 'failed'
            })
    },
})

export const {
    setLotCreationAddress,
    setLotCreationDestinationCountry,
    setLotCreationGGnotificationCOCTabId,
    setLotCreationLabelling,
    setLotCreationManipulationPeriod,
    setLotCreationProducts,
    setLotCreationTotalQuantity,
    setTabIdToUpdate,
    setTabUpdateRequest,
    setTabUpdateCocPartialTransformations,
    setTabUpdateCocTypes,
    setTabUpdateGGNLabelLogoLicense,
    setTabUpdateGGNotificationId,
    setTabUpdatePartialTransformation,
    setTabUpdateSubcontractedActivity,
    resetGetAllLotStatus,
    resetGetAllTabStatus,
    resetGetLotByIdStatus,
    resetGetTabByIdStatus,
    resetLotCreationRequest,
    resetLotCreationStatus,
    resetLotDeleteStatus,
    resetTabUpdateRequest,
    resetTabUpdateStatus
} = GGNotificationCoCSlice.actions

export default GGNotificationCoCSlice.reducer